// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// main.ts — bootstrap, screens, input and the clock.
//
// THE CLOCK IS A setInterval, NOT requestAnimationFrame. A backgrounded tab stops issuing frames, so
// a six-minute run driven by rAF quietly pauses the moment somebody checks a message and resumes
// with a wrong elapsed time. rAF here is a RENDER signal only. The interval carries the simulation
// and clamps its catch-up to one second, so returning to a tab after five minutes does not spend
// six thousand ticks in one frame.
//
// THE OVERLAY DISMISSAL CONTRACT IS FOUR WAYS OUT, ALL OF THEM WIRED: the close button, a tap
// anywhere on the backdrop, Escape, and a backdrop that is genuinely reachable rather than
// pointer-events:none. A panel with one way out is a game you cannot play, and on a phone the
// button that opened it is underneath the panel.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';
import { PATCHES } from './cues';
import { DEFAULT_MODE, ITEM_NAME, Kind, KindT, MODE_IDS, modeOf } from './modes';
import { generate } from './gen';
import { STEPS, tutorialBoard } from './tutorial';
import { View, createBoard, hottest } from './render';
import {
  COST,
  N,
  State,
  breakEven,
  canPlace,
  createState,
  flowRate,
  machineState,
  place,
  remove,
  step as stepCell,
  tick,
  tierOf,
} from './sim';
import { FOOTER, aboutHtml, gameHtml, howToPlayHtml, menuHtml, summaryHtml } from './ui';

const store = createStore('grist');
const sfx = createSfx({ muted: store.get('muted', false) as boolean, patches: PATCHES });

const app = document.getElementById('app') as HTMLElement;
let mode = (store.get('mode', DEFAULT_MODE) as string) || DEFAULT_MODE;
if (!MODE_IDS.includes(mode as (typeof MODE_IDS)[number])) mode = DEFAULT_MODE;

let state: State | null = null;
let view: View | null = null;
let timer: number | null = null;
let armed: KindT | null = null;
let erasing = false;
let strainOn = false;
let paused = false;
let tutorial = -1;
let lastTierOf = new Uint8Array(N);

const setPlaying = (on: boolean): void => {
  document.body.classList.toggle('playing', on);
};

// ── overlays ────────────────────────────────────────────────────────────────

function sheet(html: string, onClose?: () => void): HTMLElement {
  const back = document.createElement('div');
  back.className = 'sheet';
  const card = document.createElement('div');
  card.className = 'sheet-card';
  card.innerHTML = html;
  back.appendChild(card);
  document.body.appendChild(back);

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    back.remove();
    document.removeEventListener('keydown', onKey);
    onClose?.();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  card.querySelector('.close')?.addEventListener('click', close);
  // pointerdown covers mouse, touch and pen in one handler. An overlay opened FROM a pointerup would
  // otherwise be closed by its own trailing click, so the backdrop listener is armed a beat late.
  window.setTimeout(() => {
    back.addEventListener('pointerdown', (e) => {
      if (!card.contains(e.target as Node)) close();
    });
  }, 350);
  document.addEventListener('keydown', onKey);
  (back as unknown as { close: () => void }).close = close;
  return back;
}

// ── the menu ────────────────────────────────────────────────────────────────

function showMenu(): void {
  stop();
  setPlaying(false);
  app.innerHTML = `<main class="main-content">${menuHtml(mode)}</main>${FOOTER}`;
  const syncMute = (): void => {
    const b = document.getElementById('mute') as HTMLButtonElement;
    b.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
    b.setAttribute('aria-pressed', String(sfx.muted()));
  };
  syncMute();
  for (const b of Array.from(document.querySelectorAll<HTMLElement>('.mode-btn'))) {
    b.addEventListener('click', () => {
      mode = b.dataset.mode as string;
      store.set('mode', mode);
      for (const o of Array.from(document.querySelectorAll('.mode-btn'))) {
        o.setAttribute('aria-pressed', String((o as HTMLElement).dataset.mode === mode));
      }
      sfx.play('select');
    });
  }
  document.getElementById('play')?.addEventListener('click', () => {
    sfx.unlock();
    sfx.play('start');
    startRun();
  });
  document.getElementById('howto')?.addEventListener('click', () => startTutorial());
  document.getElementById('about')?.addEventListener('click', () => sheet(aboutHtml()));
  document.getElementById('mute')?.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    syncMute();
  });
}

// ── a run ───────────────────────────────────────────────────────────────────

function startRun(): void {
  const seed = newSeed();
  const b = generate(seed);
  state = createState(mode, seed, b.layout, b.quirks);
  tutorial = -1;
  mountGame();
}

function startTutorial(): void {
  sfx.unlock();
  state = createState('steady', 1, tutorialBoard(), []);
  tutorial = 0;
  mountGame();
}

function mountGame(): void {
  if (!state) return;
  setPlaying(true);
  armed = null;
  erasing = false;
  strainOn = false;
  paused = false;
  lastTierOf = new Uint8Array(N);
  app.innerHTML = `<main class="main-content">${gameHtml(state.mode)}</main>${FOOTER}`;
  view = createBoard(document.getElementById('board') as HTMLElement);
  wireControls();
  wireBoard();
  if (tutorial >= 0) showCoach();
  paint();
  start();
}

function wireControls(): void {
  for (const t of Array.from(document.querySelectorAll<HTMLElement>('.tool'))) {
    t.addEventListener('click', () => {
      const k = Number(t.dataset.kind) as KindT;
      armed = armed === k ? null : k;
      erasing = false;
      sfx.play('select');
      syncTools();
    });
  }
  document.getElementById('erase')?.addEventListener('click', () => {
    erasing = !erasing;
    armed = null;
    sfx.play('select');
    syncTools();
  });
  document.getElementById('strain')?.addEventListener('click', () => {
    strainOn = !strainOn;
    document.getElementById('strain')?.setAttribute('aria-pressed', String(strainOn));
    paint();
  });
  document.getElementById('help')?.addEventListener('click', () => sheet(howToPlayHtml(state!.mode)));
  document.getElementById('pause')?.addEventListener('click', () => {
    paused = !paused;
    (document.getElementById('pause') as HTMLElement).textContent = paused ? '▶' : 'II';
  });
  document.getElementById('coachSkip')?.addEventListener('click', () => {
    tutorial = -1;
    store.set('taught', true);
    document.getElementById('coach')?.setAttribute('hidden', '');
    paint();
  });
}

function syncTools(): void {
  for (const t of Array.from(document.querySelectorAll<HTMLElement>('.tool'))) {
    t.setAttribute('aria-pressed', String(armed === Number(t.dataset.kind)));
  }
  document.getElementById('erase')?.setAttribute('aria-pressed', String(erasing));
}

/** The tutorial refuses everything except the step's own move. */
function allowed(cell: number, kind: KindT | null): boolean {
  if (tutorial < 0) return true;
  const st = STEPS[tutorial];
  if (!st || st.watch) return false;
  if (!st.cells.includes(cell)) return false;
  if (st.erase) return true;
  return kind === st.tool || kind === Kind.Belt;
}

/**
 * Which way a tapped belt should face: toward the neighbour that can really accept an item and is
 * nearest the wharf. Ties break by compass order, so it is deterministic.
 */
function inferDir(s: State, cell: number): number {
  const wharves: number[] = [];
  for (let c = 0; c < N; c++) if (s.kind[c] === Kind.Wharf) wharves.push(c);
  const toWharf = (c: number): number => {
    let best = 99;
    for (const w of wharves) {
      const d = Math.abs((c % 12) - (w % 12)) + Math.abs(Math.floor(c / 12) - Math.floor(w / 12));
      if (d < best) best = d;
    }
    return best;
  };
  let bestDir = 2;
  let bestScore = Infinity;
  for (let d = 0; d < 4; d++) {
    const t = stepCell(cell, d);
    if (t < 0) continue;
    const k = s.kind[t];
    const accepts =
      k === Kind.Wharf ||
      k === Kind.Smelter ||
      k === Kind.Mill ||
      k === Kind.Jig ||
      ((k === Kind.Belt || k === Kind.Wye) && stepCell(t, s.dir[t]) !== cell);
    if (!accepts) continue;
    const score = toWharf(t);
    if (score < bestScore) {
      bestScore = score;
      bestDir = d;
    }
  }
  return bestDir;
}

function wireBoard(): void {
  const root = view!.root;
  let painting = false;
  let lastCell = -1;

  const act = (cell: number, prev: number): void => {
    if (!state || cell < 0) return;
    if (erasing) {
      if (!allowed(cell, null)) return void sfx.play('deny');
      if (remove(state, cell)) sfx.play('pull');
      else sfx.play('deny');
      paint();
      return;
    }
    if (armed === null) return;
    if (!allowed(cell, armed)) return void sfx.play('deny');
    // A belt DRAGGED from cell to cell points where the finger went next; a belt TAPPED on its own
    // points at whichever neighbour can actually take an item and is closer to the wharf.
    //
    // A fixed default (south) is what the first version did, and it makes tap-to-place useless the
    // moment a lane needs to turn — the tutorial's join step laid a belt pointing into bare ground,
    // no traffic ever joined the lane, and the step after it could never complete. Pointing at the
    // wharf is both the fix and the behaviour a player expects.
    let dir = prev >= 0 ? 2 : inferDir(state, cell);
    if (prev >= 0) {
      for (let d = 0; d < 4; d++) if (stepCell(prev, d) === cell) dir = d;
    }
    if (armed === Kind.Belt && prev >= 0 && state.kind[prev] === Kind.Belt) state.dir[prev] = dir;
    if (!canPlace(state, armed, cell)) return void sfx.play('deny');
    if (place(state, armed, cell, dir)) sfx.play(armed === Kind.Belt ? 'lay' : 'place');
    paint();
  };

  root.addEventListener('pointerdown', (e) => {
    const c = view!.cellAt(e.clientX, e.clientY);
    if (c < 0) return;
    root.setPointerCapture(e.pointerId);
    painting = true;
    lastCell = -1;
    act(c, -1);
    lastCell = c;
    e.preventDefault();
  });
  root.addEventListener('pointermove', (e) => {
    if (!painting) return;
    const c = view!.cellAt(e.clientX, e.clientY);
    if (c < 0 || c === lastCell) return;
    act(c, lastCell);
    lastCell = c;
  });
  const end = (): void => {
    painting = false;
    lastCell = -1;
  };
  root.addEventListener('pointerup', end);
  // A cancelled gesture is an ABORTED one, never a completed one.
  root.addEventListener('pointercancel', end);
}

// ── the clock ───────────────────────────────────────────────────────────────

let acc = 0;
let last = 0;

function start(): void {
  stop();
  last = Date.now();
  acc = 0;
  timer = window.setInterval(() => {
    const now = Date.now();
    let dt = now - last;
    last = now;
    // A backgrounded tab must not spend six thousand ticks in one frame when it comes back.
    if (dt > 1000) dt = 1000;
    if (paused || !state || state.over) return;
    if (tutorial >= 0 && STEPS[tutorial] && !STEPS[tutorial].watch) {
      // Build steps hold the CLOCK so the board cannot change under the instruction — but they must
      // still test whether the player has finished the step. The first version returned here before
      // advanceTutorial(), which meant every build step was unpassable: the board accepted the
      // placement, the instruction never changed, and the tutorial soft-locked on step 1 for
      // everybody. Found by playing it in a browser, not by any unit test — the steps were all
      // individually legal, which is exactly what tests/tutorial.test.ts checks.
      advanceTutorial();
      paint();
      return;
    }
    acc += dt;
    const speed = tutorial >= 0 ? 3 : 1;
    while (acc >= 50) {
      for (let i = 0; i < speed; i++) tick(state);
      acc -= 50;
    }
    afterTicks();
  }, 50);
}

function stop(): void {
  if (timer !== null) window.clearInterval(timer);
  timer = null;
}

function afterTicks(): void {
  if (!state) return;
  // The ground going bad is one of only two things in this game with a voice.
  for (let c = 0; c < N; c++) {
    const t = tierOf(state.rut[c]);
    if (t > lastTierOf[c]) sfx.play(t >= 4 ? 'scour' : 'sour');
    lastTierOf[c] = t;
  }
  if (tutorial >= 0) advanceTutorial();
  paint();
  if (state.over) finish();
}

function advanceTutorial(): void {
  const st = STEPS[tutorial];
  if (!st || !state) return;
  if (st.done(state)) {
    tutorial++;
    sfx.play('select');
    if (tutorial >= STEPS.length) {
      tutorial = -1;
      store.set('taught', true);
      document.getElementById('coach')?.setAttribute('hidden', '');
      return;
    }
    showCoach();
  }
}

function showCoach(): void {
  const el = document.getElementById('coach');
  if (!el || tutorial < 0) return;
  el.removeAttribute('hidden');
  (document.getElementById('coachStep') as HTMLElement).textContent = `${tutorial + 1}/${STEPS.length}`;
  (document.getElementById('coachCopy') as HTMLElement).textContent = STEPS[tutorial].copy;
}

// ── painting ────────────────────────────────────────────────────────────────

function paint(): void {
  if (!state || !view) return;
  const m = modeOf(state.mode);
  view.paint(state, { strain: strainOn });
  (document.getElementById('shipped') as HTMLElement).textContent = String(state.shipped);
  (document.getElementById('scrip') as HTMLElement).textContent = String(state.scrip);
  (document.getElementById('clockbar') as HTMLElement).style.width = `${Math.min(100, (state.tick / m.ticks) * 100)}%`;

  for (const t of Array.from(document.querySelectorAll<HTMLElement>('.tool'))) {
    const k = Number(t.dataset.kind) as KindT;
    (t as HTMLButtonElement).disabled = !m.moveOnly && state.scrip < COST[k];
  }

  const strip = document.getElementById('strip');
  if (strip) strip.innerHTML = problems(state);

  const rail = document.getElementById('rail');
  if (rail) rail.innerHTML = railHtml(state);
}

/** One chip per distinct root cause, never one per victim. */
function problems(s: State): string {
  const chips: string[] = [];
  let starved = 0;
  let blocked = 0;
  for (let c = 0; c < N; c++) {
    const k = s.kind[c];
    if (k !== Kind.Smelter && k !== Kind.Mill && k !== Kind.Jig) continue;
    const st = machineState(s, c);
    if (st === 'starved') starved++;
    if (st === 'blocked') blocked++;
  }
  const hot = hottest(s, 1)[0];
  if (hot !== undefined && tierOf(s.rut[hot]) >= 2) {
    chips.push(`<span class="chip hot">A lane is worn to tier ${tierOf(s.rut[hot])}</span>`);
  }
  if (starved) chips.push(`<span class="chip">${starved} machine${starved > 1 ? 's' : ''} starved</span>`);
  if (blocked) chips.push(`<span class="chip">${blocked} backed up</span>`);
  if (!chips.length) chips.push('<span class="chip">Everything is moving.</span>');
  return chips.slice(0, 2).join('');
}

function railHtml(s: State): string {
  const rows = hottest(s, 5)
    .map(
      (c) =>
        `<div class="row"><b>tier ${tierOf(s.rut[c])}</b> rut ${s.rut[c]}<span>${flowRate(s, c).toFixed(2)} / ${breakEven(
          s.scour[c],
        ).toFixed(2)}</span></div>`,
    )
    .join('');
  return `<b style="font-size:12px">Hardest-worked ground</b><div class="rows">${rows}</div>
    <p style="font-size:11.5px;color:var(--text-dim)">An armature is ${ITEM_NAME[2]} + ${ITEM_NAME[3]} — three shale and two loam.</p>`;
}

function finish(): void {
  stop();
  setPlaying(false);
  sfx.play('whistle');
  const best = Number(store.get(`best:${state!.mode}`, 0));
  if (state!.shipped > best) store.set(`best:${state!.mode}`, state!.shipped);
  const panel = sheet(summaryHtml(state!, best));
  const closePanel = (panel as unknown as { close: () => void }).close;
  panel.querySelector('#again')?.addEventListener('click', () => {
    closePanel();
    startRun();
  });
  panel.querySelector('#tomenu')?.addEventListener('click', () => {
    closePanel();
    showMenu();
  });
  panel.querySelector('#fb')?.addEventListener('click', (e) => {
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement });
  });
}

// ── boot ────────────────────────────────────────────────────────────────────

hardenViewport();
if (!store.get('taught', false)) startTutorial();
else showMenu();

window.addEventListener('pagehide', stop);
